import React, { useState } from 'react';
import { FileDown, Building2, User, Briefcase, IndianRupee, MapPin, ChevronRight, ChevronLeft, Plus, Trash2, CheckCircle } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - matching Form_Q.xlsx template placeholders exactly
const mockFormData = {
  // Establishment fields
  est_name: "ABC Manufacturing Pvt Ltd",
  est_address1: "123 Industrial Area, Whitefield",
  est_address2: "",
  est_city: "Bangalore",
  est_pincode: "560066",

  // Employer fields
  empl_name: "Mr. Rajesh Kumar - Managing Director",
  empl_address1: "ABC Manufacturing Pvt Ltd",
  empl_address2: "Corporate Office, MG Road",
  empl_city: "Bangalore",
  empl_pincode: "560001",

  // Date and location
  current_date: "2025-01-15",
  current_city: "Bangalore",

  // Employee array (Form Q can have multiple employees)
  employees: [
    {
      emp_id: "EMP/2025/156",
      emp_fname: "Amit Kumar Sharma",
      emp_lname: "Sharma",
      emp_dob: "1990-08-15",
      emp_doj: "2025-01-15",
      emp_desig: "Production Supervisor",
      emp_nature_work: "Supervising production floor operations, quality control",
      emp_address: "45 MG Road, Koramangala, Bangalore - 560034",
      emp_perm_address: "12 Gandhi Street, Malleswaram, Bangalore - 560003",
      emp_basic: "25000",
      emp_da: "3000",
      emp_otherallow: "2000",
      emp_gross: "30000"
    }
  ]
};

const AppointmentOrderForm = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Fetch data from API on component mount
  React.useEffect(() => {
    const fetchFormData = async () => {
      setIsLoadingData(true);
      try {
        const token = localStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${baseUrl}/api/forms/form_q?establishment_id=1&month=${new Date().getMonth() + 1}&year=${new Date().getFullYear()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });
        if (!response.ok) {
          throw new Error(`API failed with status: ${response.status}`);
        }
        const apiData = await response.json();

        if (apiData && apiData.employees && Array.isArray(apiData.employees)) {
          setFormData(apiData);
          console.log('Form Q data loaded from API:', apiData.employees.length, 'employees');
        } else if (apiData) {
          // Transform single response to form format
          setFormData({ ...mockFormData, ...apiData });
          console.log('Form Q data loaded from API');
        } else {
          console.warn('API response format unexpected, using mock data');
        }
      } catch (error) {
        console.error('Error fetching form data:', error);
        console.log('Using mock data with 1 employee');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeChange = (index, field, value) => {
    const updatedEmployees = [...formData.employees];
    updatedEmployees[index] = {
      ...updatedEmployees[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      employees: updatedEmployees
    }));
  };

  const addEmployee = () => {
    const newEmployee = {
      emp_id: "",
      emp_fname: "",
      emp_lname: "",
      emp_dob: "",
      emp_doj: "",
      emp_desig: "",
      emp_nature_work: "",
      emp_address: "",
      emp_perm_address: "",
      emp_basic: "",
      emp_da: "",
      emp_otherallow: "",
      emp_gross: ""
    };
    setFormData(prev => ({
      ...prev,
      employees: [...prev.employees, newEmployee]
    }));
  };

  const removeEmployee = (index) => {
    if (formData.employees.length > 1) {
      const updatedEmployees = formData.employees.filter((_, idx) => idx !== index);
      setFormData(prev => ({ ...prev, employees: updatedEmployees }));
      // If we're on a step beyond the new employee count, go back
      if (currentStep > updatedEmployees.length) {
        setCurrentStep(updatedEmployees.length);
      }
    }
  };

  const nextStep = () => {
    const totalSteps = 1 + formData.employees.length; // 1 (organization) + employees
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Calculate gross salary
  const calculateGross = (employee) => {
    const basic = parseFloat(employee.emp_basic) || 0;
    const da = parseFloat(employee.emp_da) || 0;
    const other = parseFloat(employee.emp_otherallow) || 0;
    return (basic + da + other).toString();
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('=== Starting Form Q - Appointment Order Generation ===');
      console.log('Loading template...');
      const response = await fetch('/templates/Form_Q.xlsx');
      if (!response.ok) {
        throw new Error('Failed to load template');
      }
      const arrayBuffer = await response.arrayBuffer();

      console.log('Creating workbook...');
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        throw new Error('No worksheet found in template');
      }

      console.log(`\n=== Processing ${formData.employees.length} employees ===`);

      // Remove ALL background fills from entire worksheet
      console.log('\n=== Removing ALL background fills from worksheet ===');
      let fillsRemoved = 0;
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
            fillsRemoved++;
          }
        });
      });
      console.log(`Removed ${fillsRemoved} cell backgrounds`);

      // First pass: Replace organization/employer/date placeholders in ALL rows
      console.log('\n=== Pass 1: Replacing form-level fields ===');
      const missingVars = new Set();

      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;
            let hasReplacement = false;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              // Normalize field name: replace spaces with underscores for lookup
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check form-level fields
              if (formData[normalizedFieldName] !== undefined) {
                const replacementValue = formData[normalizedFieldName];
                console.log(`Row ${rowNumber}, Col ${colNumber}: {{ ${fieldName} }} → "${replacementValue}"`);
                newValue = newValue.replace(match[0], replacementValue);
                hasReplacement = true;
              } else {
                // Track missing variables (will be filled in pass 2 for employee fields)
                if (!fieldName.startsWith('emp_')) {
                  missingVars.add(fieldName);
                  console.warn(`Row ${rowNumber}, Col ${colNumber}: {{ ${fieldName} }} → ⚠️ NOT FOUND in formData`);
                }
              }
            }

            if (hasReplacement) {
              cell.value = newValue;
            }
          }
        });
      });

      // Second pass: Replace employee-specific placeholders for the first employee only
      // (Form Q doesn't have repeating rows like Form S)
      console.log('\n=== Pass 2: Replacing employee fields ===');
      const employeeMissingVars = new Set();
      const employee = formData.employees[0]; // Use first employee for single appointment order

      // Update gross if not set
      if (!employee.emp_gross || employee.emp_gross === "") {
        employee.emp_gross = calculateGross(employee);
      }

      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;
            let hasReplacement = false;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check employee fields
              if (employee[normalizedFieldName] !== undefined) {
                const replacementValue = employee[normalizedFieldName];
                console.log(`Row ${rowNumber}, Col ${colNumber}: {{ ${fieldName} }} → "${replacementValue}"`);
                newValue = newValue.replace(match[0], replacementValue);
                hasReplacement = true;
              } else if (fieldName.startsWith('emp_')) {
                employeeMissingVars.add(fieldName);
                console.warn(`Row ${rowNumber}, Col ${colNumber}: {{ ${fieldName} }} → ⚠️ NOT FOUND`);
              }
            }

            if (hasReplacement) {
              cell.value = newValue;
            }
          }
        });
      });

      if (employeeMissingVars.size > 0) {
        console.warn('\n⚠️ Missing Employee Variables:', Array.from(employeeMissingVars));
      }

      console.log('\n=== Generating Excel file ===');
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-Q-Appointment-Order-${employee.emp_fname}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setIsGenerating(false);
      console.log('=== Generation Complete! ===');
      alert(`Form Q - Appointment Order generated successfully for ${employee.emp_fname}!`);
    } catch (error) {
      console.error('Error generating Excel:', error);
      setIsGenerating(false);
      alert(`Error generating Excel: ${error.message}`);
    }
  };

  // Calculate total steps: 1 (establishment) + employees
  const totalSteps = 1 + formData.employees.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Render step content
  const renderStepContent = () => {
    // Step 0: Establishment & Employer
    if (currentStep === 0) {
      return (
        <>
          <div className="form-section">
            <div className="section-header">
              <Building2 size={20} />
              <h3>Organization Information</h3>
            </div>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Organization Name (est_name)</label>
                <input
                  type="text"
                  value={formData.est_name}
                  onChange={(e) => handleInputChange('est_name', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Address Line 1 (est_address1)</label>
                <input
                  type="text"
                  value={formData.est_address1}
                  onChange={(e) => handleInputChange('est_address1', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Address Line 2 (est_address2)</label>
                <input
                  type="text"
                  value={formData.est_address2}
                  onChange={(e) => handleInputChange('est_address2', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>City (est_city)</label>
                <input
                  type="text"
                  value={formData.est_city}
                  onChange={(e) => handleInputChange('est_city', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Pincode (est_pincode)</label>
                <input
                  type="text"
                  value={formData.est_pincode}
                  onChange={(e) => handleInputChange('est_pincode', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <User size={20} />
              <h3>Employer Information</h3>
            </div>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Employer Name & Designation (empl_name)</label>
                <input
                  type="text"
                  value={formData.empl_name}
                  onChange={(e) => handleInputChange('empl_name', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Address Line 1 (empl_address1)</label>
                <input
                  type="text"
                  value={formData.empl_address1}
                  onChange={(e) => handleInputChange('empl_address1', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Address Line 2 (empl_address2)</label>
                <input
                  type="text"
                  value={formData.empl_address2}
                  onChange={(e) => handleInputChange('empl_address2', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>City (empl_city)</label>
                <input
                  type="text"
                  value={formData.empl_city}
                  onChange={(e) => handleInputChange('empl_city', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Pincode (empl_pincode)</label>
                <input
                  type="text"
                  value={formData.empl_pincode}
                  onChange={(e) => handleInputChange('empl_pincode', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <MapPin size={20} />
              <h3>Document Details</h3>
            </div>
            <div className="form-grid">
              <div className="form-field">
                <label>Current Date (current_date)</label>
                <DateInput
                  value={formData.current_date}
                  onChange={(e) => handleInputChange('current_date', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Current City (current_city)</label>
                <input
                  type="text"
                  value={formData.current_city}
                  onChange={(e) => handleInputChange('current_city', e.target.value)}
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    // Steps 1 to N: Employee details
    if (currentStep > 0 && currentStep <= formData.employees.length) {
      const employeeIndex = currentStep - 1;
      const employee = formData.employees[employeeIndex];

      return (
        <>
          <div className="form-section" style={{ background: 'var(--theme-surface)', border: '1px solid #e5e7eb' }}>
            <div className="section-header" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <User size={20} />
                <h3>Employee {employeeIndex + 1} - Appointment Details</h3>
              </div>
              {formData.employees.length > 1 && (
                <button
                  onClick={() => removeEmployee(employeeIndex)}
                  style={{
                    background: '#ef4444',
                    color: 'var(--theme-surface, #ffffff)',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '0.5rem 1rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem'
                  }}
                >
                  <Trash2 size={16} />
                  Remove Employee
                </button>
              )}
            </div>
            <div className="form-grid">
              <div className="form-field">
                <label>Employee ID (emp_id)</label>
                <input
                  type="text"
                  value={employee.emp_id}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_id', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Full Name (emp_fname)</label>
                <input
                  type="text"
                  value={employee.emp_fname}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_fname', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Last Name (emp_lname)</label>
                <input
                  type="text"
                  value={employee.emp_lname}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_lname', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Date of Birth (emp_dob)</label>
                <DateInput
                  value={employee.emp_dob}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_dob', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Date of Joining (emp_doj)</label>
                <DateInput
                  value={employee.emp_doj}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_doj', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Designation (emp_desig)</label>
                <input
                  type="text"
                  value={employee.emp_desig}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_desig', e.target.value)}
                />
              </div>
              <div className="form-field full-width">
                <label>Nature of Work (emp_nature_work)</label>
                <textarea
                  value={employee.emp_nature_work}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_nature_work', e.target.value)}
                  rows={2}
                />
              </div>
              <div className="form-field full-width">
                <label>Current Address (emp_address)</label>
                <textarea
                  value={employee.emp_address}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_address', e.target.value)}
                  rows={2}
                />
              </div>
              <div className="form-field full-width">
                <label>Permanent Address (emp_perm_address)</label>
                <textarea
                  value={employee.emp_perm_address}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_perm_address', e.target.value)}
                  rows={2}
                />
              </div>
              <div className="form-field">
                <label>Basic Wage (emp_basic)</label>
                <input
                  type="number"
                  value={employee.emp_basic}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_basic', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>DA (emp_da)</label>
                <input
                  type="number"
                  value={employee.emp_da}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_da', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Other Allowances (emp_otherallow)</label>
                <input
                  type="number"
                  value={employee.emp_otherallow}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_otherallow', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Gross Salary (emp_gross)</label>
                <input
                  type="text"
                  value={employee.emp_gross || calculateGross(employee)}
                  readOnly
                  className="calculated-field"
                />
              </div>
            </div>
          </div>

          {/* Add Another Employee Button */}
          <button
            onClick={() => {
              addEmployee();
              setCurrentStep(formData.employees.length + 1);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#10b981',
              color: 'var(--theme-surface, #ffffff)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginTop: '1.5rem'
            }}
          >
            <Plus size={20} />
            Add Another Employee
          </button>
        </>
      );
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form Q - Appointment Order</h2>
          <p className="appointment-subtitle">
            Official Appointment Letter under Shops & Organizations Act
          </p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {currentStep === 0 ? 'Organization & Employer Info' :
               `Employee ${currentStep}`}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: currentStep === 0 ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
              color: currentStep === 0 ? '#9ca3af' : '#374151',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          {currentStep === totalSteps - 1 ? (
            <button
              onClick={generateExcel}
              disabled={isGenerating}
              className={`btn-generate ${isGenerating ? 'generating' : ''}`}
            >
              {isGenerating ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                  </svg>
                  <span>Generating Excel...</span>
                </>
              ) : (
                <>
                  <FileDown size={20} />
                  <span>Generate Form</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextStep}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                color: 'var(--theme-surface, #ffffff)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Next
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentOrderForm;
